import { RegionId as PrismaRegionId } from '@prisma/client';
import { type ReportType } from 'report/types';

export const regionIds = Object.values(PrismaRegionId);

export type RegionId = PrismaRegionId;

export function getRegionId(value: string | null | undefined): RegionId | null {
    if (!value) {
        return null;
    }

    if (!regionIds.includes(value as RegionId)) {
        return null;
    }

    return value as RegionId;
}

// these region names are not localized
export const regionLabels: Record<RegionId, string> = {
    au: 'Australia',
    ca: 'Canada',
    latam: 'LATAM',
    nl: 'Netherlands',
    nz: 'New Zealand',
    us: 'United States',
};

// these region reporting titles are localized
export const regionTitles: Record<RegionId, string> = {
    au: 'Australian Podcast Ranker',
    ca: 'Canada Podcast Ranker',
    latam: 'Latin America Podcast Ranker',
    nl: 'Netherlands Podcast Ranker',
    nz: 'New Zealand Podcast Ranker',
    us: 'US Podcast Ranker',
};

// the order of the reportSets is important as the first report set of a regionId is the default report set
export const reportSets = defineReportSets([
    {
        regionId: 'au',
        slug: 'podcasts',
        reportType: 'podcastAu' as const,
        title: 'Top Podcasts',
    },
    {
        regionId: 'au',
        slug: 'podcasts-aus',
        reportType: 'podcastAu' as const,
        title: 'Top Podcasts (Australian Made)',
    },
    {
        regionId: 'au',
        slug: 'demos-plus-podcasts',
        reportType: 'demosPlusPodcastAu' as const,
        title: 'Demos+ Category Ranker',
    },
    {
        regionId: 'au',
        slug: 'demos-plus-program',
        reportType: 'demosPlusProgramAu' as const,
        title: 'Demos+ Top Indexing Programs by Audience Composition',
    },
    {
        regionId: 'ca',
        slug: 'podcasts',
        reportType: 'podcastUs' as const,
        title: 'Top Podcasts',
    },
    {
        regionId: 'ca',
        slug: 'networks',
        reportType: 'networkCa' as const,
        title: 'Top Sales Networks',
    },
    {
        regionId: 'ca',
        slug: 'podcasts-english',
        reportType: 'podcastUs' as const,
        title: 'Canadian Made English Podcasts',
    },
    {
        regionId: 'ca',
        slug: 'podcasts-french',
        reportType: 'podcastUs' as const,
        title: 'Canadian Made French Podcasts',
    },
    {
        regionId: 'latam',
        slug: 'podcasts',
        reportType: 'podcastLatam' as const,
        title: 'Top Podcasts',
    },
    {
        regionId: 'latam',
        slug: 'networks',
        reportType: 'networkLatam' as const,
        title: 'Top Sales Networks',
    },
    {
        regionId: 'nl',
        slug: 'podcasts',
        reportType: 'podcastNl' as const,
        title: 'Top Podcasts',
    },
    {
        regionId: 'nl',
        slug: 'networks',
        reportType: 'networkNl' as const,
        title: 'Top Networks',
    },
    {
        regionId: 'nz',
        slug: 'podcasts',
        reportType: 'podcastNz' as const,
        title: 'Top Podcasts',
    },
    {
        regionId: 'nz',
        slug: 'networks',
        reportType: 'networkNz' as const,
        title: 'Top Networks',
    },
    {
        regionId: 'us',
        slug: 'podcasts-survey',
        reportType: 'podcastSurveyUs' as const,
        title: 'Top Podcasts By Listeners/Viewers (Survey)',
    },
    {
        regionId: 'us',
        slug: 'demo-plus-survey',
        reportType: 'demosPlusPodcastUs' as const,
        title: 'Demos + Category Ranker',
    },
    {
        regionId: 'us',
        slug: 'demo-plus-program',
        reportType: 'demosPlusProgramUs' as const,
        title: 'Demos+ Top Indexing Programs by Audience Composition',
    },
    {
        regionId: 'us',
        slug: 'podcasts-weekly-downloads',
        reportType: 'podcastUs' as const,
        title: 'Top Podcasts By Weekly Average Downloads',
    },
    {
        regionId: 'us',
        slug: 'networks-weekly-downloads',
        reportType: 'networkUsDownloads' as const,
        title: 'Top Sales Networks By Weekly Average Downloads',
    },
    {
        regionId: 'us',
        slug: 'networks-weekly-users',
        reportType: 'networkUsUsers' as const,
        title: 'RETIRED - Top Sales Networks By Weekly Average Users',
    },
    {
        regionId: 'us',
        slug: 'podcasts-weekly-users',
        reportType: 'podcastUs' as const,
        title: 'RETIRED - Top Podcasts By Weekly Average Users',
    },
    // AU retired reports moved to bottom
    {
        regionId: 'au',
        slug: 'networks',
        reportType: 'networkAu' as const,
        title: 'RETIRED - Top Publishers',
    },
    {
        regionId: 'au',
        slug: 'salesreps',
        reportType: 'salesRepresentativeAu' as const,
        title: 'RETIRED - Top Sales Representatives',
    },
]);

export type ReportSet = (typeof reportSets)[number];

export function getReportSetsByRegionId<T extends RegionId>(regionId: T): Extract<ReportSet, { regionId: T }>[] {
    // we have to assert that .filter returns what the return type expects
    return reportSets.filter((reportSet) => reportSet.regionId === regionId) as Extract<ReportSet, { regionId: T }>[];
}

export function getReportSetBySlug(regionId: RegionId, slug: string): ReportSet | null {
    return reportSets.find((reportSet) => reportSet.regionId === regionId && reportSet.slug === slug) ?? null;
}

export function getDefaultReportSet(regionId: RegionId): ReportSet | null {
    // get the first reportSet that matches the specified regionId
    return reportSets.find((reportSet) => reportSet.regionId === regionId) ?? null;
}

export function getReportType(reportSet: ReportSet): ReportType {
    return reportSet.reportType;
}

export function getReportSetTitle(reportSet: ReportSet): string {
    return reportSet.title;
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
function defineReportSets<
    Slug extends string,
    RT extends ReportType,
    T extends { regionId: RegionId; slug: Slug; reportType: RT; title: string },
>(t: T[]): T[] {
    return t;
}

const QUARTER_REPORTS: ReportSet['slug'][] = ['podcasts-survey', 'demo-plus-survey', 'demo-plus-program'] as const;

export function isQuarterReport(reportSet: ReportSet): boolean {
    return QUARTER_REPORTS.includes(reportSet.slug);
}

const SEMI_ANNUAL_REPORTS: ReportSet['slug'][] = ['demos-plus-program'] as const;

export function isSemiAnnualReport(reportSet: ReportSet): boolean {
    return SEMI_ANNUAL_REPORTS.includes(reportSet.slug);
}

export type ReportCadence = 'monthly' | 'quarterly' | 'semiAnnual';

/**
 * Quarterly and semi-annual reports store their period as the first month of the period
 * (1/4/7/10 for quarters, 1/7 for halves), so a period is only meaningful within a single cadence.
 */
export function getReportCadence(reportSet: ReportSet): ReportCadence {
    if (isSemiAnnualReport(reportSet)) {
        return 'semiAnnual';
    }

    if (isQuarterReport(reportSet)) {
        return 'quarterly';
    }

    return 'monthly';
}

export function reportSetsShareCadence(a: ReportSet, b: ReportSet): boolean {
    return getReportCadence(a) === getReportCadence(b);
}

const DISABLED_REPORT_SETS: Pick<ReportSet, 'regionId' | 'slug'>[] = [
    { regionId: 'us', slug: 'podcasts-weekly-users' },
    { regionId: 'us', slug: 'networks-weekly-users' },
    { regionId: 'au', slug: 'salesreps' },
    { regionId: 'au', slug: 'networks' },
] as const;

export function isReportSetDisabled(reportSet: ReportSet): boolean {
    return DISABLED_REPORT_SETS.some(
        (disabledReportSet) =>
            disabledReportSet.regionId === reportSet.regionId && disabledReportSet.slug === reportSet.slug,
    );
}
